import posthog from "posthog-js";
import { env } from "./env";

/**
 * Initialize PostHog error reporting for the admin console (#828).
 *
 * Admin is an internal staff tool, not part of the customer-facing
 * free-to-paid conversion funnel apps/web instruments (see
 * docs/posthog-setup.md) — this wiring exists so render-time crashes reach a
 * real backend instead of only `console.error` (#803's TODO), not to add
 * product-analytics/experiment tracking to the admin console. Mirrors
 * apps/web's `lib/posthog.ts` init pattern exactly (same env vars, same
 * env-gated no-op) so both apps share one PostHog integration shape.
 *
 * Should be called once when the app starts.
 */
export function initPostHog(): void {
  if (!env.VITE_POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.info("[PostHog] No API key configured - analytics disabled");
    }
    return;
  }

  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host: env.VITE_POSTHOG_HOST,
    // Admin has no conversion funnel to instrument — capture only what's
    // needed to receive error reports; no autocaptured clicks/pageviews.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    // Disable in development unless explicitly enabled (same opt-out as web).
    loaded: (ph) => {
      if (import.meta.env.DEV && !env.VITE_POSTHOG_DEV_ENABLED) {
        ph.opt_out_capturing();
      }
    },
    persistence: "localStorage+cookie",
  });
}

/**
 * Check if PostHog is initialized and capturing.
 * @returns Whether PostHog is enabled and capturing
 */
export function isPostHogEnabled(): boolean {
  return Boolean(env.VITE_POSTHOG_KEY) && !posthog.has_opted_out_capturing();
}

/**
 * Report a render error caught by a route-level error boundary (#803, #828).
 *
 * Sends ONLY the error object (name/message/stack) plus React's component
 * stack — deliberately no user identifiers, auth tokens, or app state, so a
 * crash report can never become an info-disclosure channel. No-ops when
 * PostHog is not configured/capturing (e.g. local dev without a key).
 *
 * @param error - The error thrown during render
 * @param componentStack - React-supplied component stack from componentDidCatch
 */
export function captureRouteException(
  error: Error,
  componentStack: string | null
): void {
  if (!isPostHogEnabled()) return;
  posthog.captureException(error, {
    component_stack: componentStack ?? undefined,
    error_source: "route_error_boundary",
  });
}

export { posthog };
